import type { WorkCategory } from './workHealth';
import { ProjectTelemetry } from '../../sentryConsole';
import { InputCommandQueue } from './inputCommandQueue';
import {
	initialLoadingProgress,
	isNewerLoadingProgress,
	type LoadingProgress
} from './loadingProgress';
/**
 * ProjectHandle: the main thread's facade over one project instance living in
 * the engine worker. Method names mirror the old WasmAqueductProject surface
 * so call sites stay familiar; the semantic split is:
 *
 * - commands: synchronous fire-and-forget posts (all mutations and input);
 * - queries: async (everything that returns engine data);
 * - subscriptions: coalesced packets pushed by the worker (document state,
 *   editor tick payload, diagnostics, hover pixels, presentation notices).
 */

import type { EngineBroker } from './broker';
import { bootTimelineLog } from './broker';
import type {
	AttachSurface,
	EditorPointerCmd,
	FramePacket,
	LandingDemoLoop,
	MainToWorker,
	MaterialViewportParams,
	PreviewParams,
	RendererPipelineProfile,
	SurfaceKind
} from './protocol';
import type { PhoneResolutionLoadChoice, PhoneResolutionLoadState } from '../resolutionLoading';

export type PortalAction =
	| 'place-exit'
	| 'rename'
	| 'reveal-entrance'
	| 'reveal-next-exit'
	| 'frame';

export type EditorTickPayload = {
	panelOffset?: number;
	rasterWidth?: number;
	rasterHeight?: number;
	cursor?: string;
	runs?: unknown[];
	notes?: unknown[];
	events?: Array<Record<string, unknown>>;
};

export type FrameSubscriber = {
	onArtifactErrors?: (errors: Record<string, string>) => void;
	onLoadingProgress?: (state: LoadingProgress) => void;
	onStateChange?: (contentJson: string) => void;
	onHistoryChange?: (canUndo: boolean, canRedo: boolean) => void;
	onConnectionChange?: (state: string) => void;
	onSyncStatus?: (status: import('./reliableSync').SyncStatus) => void;
	onEditorTick?: (payload: EditorTickPayload) => void;
	onOutputTrayTick?: (payload: EditorTickPayload) => void;
	onDiagnostics?: (diagnosticsJson: string) => void;
	onResolvedFormats?: (resolvedFormatsJson: string) => void;
	onHoverPixel?: (pixel: unknown) => void;
	onMaterialViewportTriangles?: (triangles: number) => void;
	onPresented?: (kinds: SurfaceKind[]) => void;
	/** First render's GPU work completed or failed: reveal notice. */
	onFirstRenderComplete?: () => void;
	onRenderError?: (message: string) => void;
	onTransport?: (state: import('../animation').TransportState) => void;
	onAnimationFps?: (fps: number | null) => void;
	onGraphProfile?: (profile: import('./protocol').GraphProfileSnapshot) => void;
	onHasTimeExpressions?: (hasTimeExpressions: boolean) => void;
	onPhoneResolutionLoad?: (state: PhoneResolutionLoadState) => void;
};

export class ProjectHandle {
	readonly telemetry: ProjectTelemetry;
	effectivePrimaryInputs: Record<string, string | null> = {};

	closed = false;
	clipboardLifetime: string = crypto.randomUUID();
	loadingProgress: LoadingProgress = initialLoadingProgress();
	/** Latest editor view transform [tx, ty, scale], mirrored from the worker
	 *  so screenToGraph stays synchronous. */
	viewTransform: [number, number, number] | null = null;
	private editorPresentationId = 0;
	private editorPresenter: {
		id: number;
		foreground: ImageBitmapRenderingContext;
	} | null = null;
	private subscribers = new Set<FrameSubscriber>();
	private terminalError: string | null = null;
	private inputCommands: InputCommandQueue;

	constructor(
		private broker: EngineBroker,
		readonly projectId: number
	) {
		this.telemetry = new ProjectTelemetry(projectId);
		this.broker.health.register(projectId);
		this.inputCommands = new InputCommandQueue((error: unknown): void => {
			this.broker.health.observer.failure(
				projectId,
				'preparation',
				'input_preparation_pending',
				error
			);
		});
		this.broker.health.inputProbe(projectId, () => this.inputCommands.snapshot());
	}

	markClosed() {
		this.broker.health.end(this.projectId, 'project_closed');
		this.closed = true;
		this.editorPresenter = null;
		this.inputCommands.close();
		this.subscribers.clear();
	}

	/** Resolves once this instance's engine is initialized in the worker. */
	whenReady(): Promise<void> {
		return this.query('engineReady').then(() => undefined);
	}

	private animationFps: number | null = null;
	private graphProfile: import('./protocol').GraphProfileSnapshot | null = null;
	subscribe(subscriber: FrameSubscriber): () => void {
		this.subscribers.add(subscriber);
		subscriber.onAnimationFps?.(this.animationFps);
		if (this.graphProfile) subscriber.onGraphProfile?.(this.graphProfile);
		subscriber.onLoadingProgress?.(this.loadingProgress);
		if (this.terminalError) {
			subscriber.onRenderError?.(this.terminalError);
			subscriber.onFirstRenderComplete?.();
		}
		return () => this.subscribers.delete(subscriber);
	}

	receiveTerminalError(error: Error) {
		this.terminalError = error.message;
		this.loadingProgress = {
			...this.loadingProgress,
			sequence: this.loadingProgress.sequence + 1,
			status: 'failed',
			failure: { stage: 'shaders', category: 'worker', retryable: false },
			stages: { ...this.loadingProgress.stages, shaders: { status: 'failed' } }
		};
		for (const subscriber of this.subscribers) subscriber.onLoadingProgress?.(this.loadingProgress);
		for (const subscriber of this.subscribers) {
			subscriber.onRenderError?.(error.message);
			subscriber.onFirstRenderComplete?.();
		}
	}

	receiveFrame(packet: FramePacket) {
		const frame = packet.editorFrame;
		let imageTick: EditorTickPayload | null = null;
		let presentedSequence: number | null = null;
		if (frame) {
			try {
				const presenter = this.editorPresenter;
				if (!this.closed && !this.terminalError && presenter?.id === frame.presentationId) {
					imageTick = JSON.parse(frame.tickJson) as EditorTickPayload;
					imageTick.panelOffset = frame.panelOffset;
					imageTick.rasterWidth = frame.foreground.width;
					imageTick.rasterHeight = frame.foreground.height;
					const present = (context: ImageBitmapRenderingContext, bitmap: ImageBitmap): void => {
						if (context.canvas.width !== bitmap.width) context.canvas.width = bitmap.width;
						if (context.canvas.height !== bitmap.height) context.canvas.height = bitmap.height;
						context.transferFromImageBitmap(bitmap);
					};
					present(presenter.foreground, frame.foreground);
					if (frame.healthGeneration === this.broker.health.generation(this.projectId))
						presentedSequence = frame.sequence;
					packet.viewTransformJson = frame.viewTransformJson;
				}
			} finally {
				frame.foreground.close();
				this.broker.post({
					op: 'editorFrameConsumed',
					projectId: this.projectId,
					sequence: frame.sequence
				});
			}
		}
		if (this.closed || this.terminalError) return;
		if (packet.transport) for (const sub of this.subscribers) sub.onTransport?.(packet.transport);
		if (
			packet.loadingProgress &&
			isNewerLoadingProgress(packet.loadingProgress, this.loadingProgress)
		) {
			this.loadingProgress = packet.loadingProgress;
			for (const sub of this.subscribers) sub.onLoadingProgress?.(this.loadingProgress);
		}
		if (packet.presented) {
			for (const kind of packet.presented) {
				bootTimelineLog(`engine: first present ${kind}`, { projectId: this.projectId });
			}
		}
		if (packet.viewTransformJson) {
			try {
				const t = JSON.parse(packet.viewTransformJson);
				if (Array.isArray(t)) this.viewTransform = t as [number, number, number];
			} catch {
				/* transform is advisory */
			}
		}
		let tick: EditorTickPayload | null = null;
		if (packet.editorTickJson) {
			try {
				tick = JSON.parse(packet.editorTickJson);
				if (tick) tick.panelOffset = packet.editorPanelOffset;
			} catch {
				tick = null;
			}
		}
		// Input events and cursor updates are delivered independently of image
		// back-pressure. A delayed image must never replay either from its snapshot.
		if (imageTick) tick = { ...imageTick, cursor: tick?.cursor, events: tick?.events ?? [] };
		let outputTrayTick: EditorTickPayload | null = null;
		if (packet.outputTrayTickJson) {
			try {
				outputTrayTick = JSON.parse(packet.outputTrayTickJson);
			} catch {
				outputTrayTick = null;
			}
		}
		if (packet.effectivePrimaryInputsJson)
			this.effectivePrimaryInputs = JSON.parse(packet.effectivePrimaryInputsJson);
		if (packet.animationFps !== undefined) this.animationFps = packet.animationFps;
		if (packet.graphProfile) this.graphProfile = packet.graphProfile;
		for (const sub of this.subscribers) {
			if (packet.phoneResolutionLoad) {
				sub.onPhoneResolutionLoad?.(packet.phoneResolutionLoad);
			}
			if (packet.stateJson) sub.onStateChange?.(packet.stateJson);
			if (packet.canUndo !== undefined && packet.canRedo !== undefined) {
				sub.onHistoryChange?.(packet.canUndo, packet.canRedo);
			}
			if (packet.connectionState) sub.onConnectionChange?.(packet.connectionState);
			if (packet.syncStatus) sub.onSyncStatus?.(packet.syncStatus);
			if (tick) sub.onEditorTick?.(tick);
			if (outputTrayTick) sub.onOutputTrayTick?.(outputTrayTick);
			if (packet.diagnosticsJson) sub.onDiagnostics?.(packet.diagnosticsJson);
			if (packet.artifactErrors) sub.onArtifactErrors?.(packet.artifactErrors);
			if (packet.resolvedFormatsJson) sub.onResolvedFormats?.(packet.resolvedFormatsJson);
			if (packet.hoverPixel) sub.onHoverPixel?.(packet.hoverPixel);
			if (packet.materialViewportTriangles !== undefined) {
				sub.onMaterialViewportTriangles?.(packet.materialViewportTriangles);
			}
			if (packet.presented) sub.onPresented?.(packet.presented);
			if (packet.firstRenderComplete) sub.onFirstRenderComplete?.();
			if (packet.renderError) sub.onRenderError?.(packet.renderError);
			if (packet.graphProfile) sub.onGraphProfile?.(packet.graphProfile);
			if (packet.animationFps !== undefined) sub.onAnimationFps?.(packet.animationFps);
			if (packet.hasTimeExpressions !== undefined) {
				sub.onHasTimeExpressions?.(packet.hasTimeExpressions);
			}
		}
		if (presentedSequence !== null) this.broker.health.presented(this.projectId, presentedSequence);
	}

	// --- Generic dispatch -------------------------------------------------

	/** Deliver input after any earlier asynchronous edit preparation. */
	private postInput(message: MainToWorker): void {
		const prepared: MainToWorker = this.broker.health.prepare(message);
		this.inputCommands.enqueue(() => this.broker.post(prepared));
	}

	/** Fire-and-forget call of a project method in the worker. */
	cmd(method: string, ...args: unknown[]) {
		this.postInput({ op: 'cmd', projectId: this.projectId, method, args });
	}

	/** Reserve an authored input before its asynchronous value is available. */
	prepareInput<T>(value: Promise<T>, commit: (value: T) => void): void {
		let release!: () => void;
		let reject!: (error: unknown) => void;
		void this.observeWork(
			'preparation',
			() =>
				new Promise<void>((resolve, fail) => {
					release = resolve;
					reject = fail;
				})
		).catch(() => {});
		this.inputCommands.prepare(
			value,
			(result: T): void => {
				try {
					commit(result);
					release();
				} catch (error: unknown) {
					reject(error);
				}
			},
			reject,
			release
		);
	}

	setWorkInteraction(category: WorkCategory, active: boolean): void {
		this.broker.health.interaction(this.projectId, category, active);
	}
	reportWorkFailure(error: unknown, category: WorkCategory = 'graph'): void {
		this.broker.health.observer.failure(this.projectId, category, 'presentation_pending', error);
	}

	reportRejectedWork(category: WorkCategory): void {
		if (this.closed) return;
		const token = this.broker.health.observer.begin(this.projectId, category);
		this.broker.health.observer.failure(
			this.projectId,
			category,
			'worker_operation_pending',
			new Error('Action could not be dispatched because the project is not ready'),
			'command_rejected'
		);
		this.broker.health.observer.complete(token, true);
	}

	observeWork<T>(
		category: WorkCategory,
		run: () => Promise<T>,
		expectedRejection: boolean = false
	): Promise<T> {
		return this.broker.health.task(this.projectId, category, run, expectedRejection);
	}

	/** Awaited call of a project method (or property) in the worker. */
	query<T = unknown>(
		method: string,
		args: unknown[] = [],
		transfer: Transferable[] = []
	): Promise<T> {
		const message: MainToWorker = this.broker.health.prepare({
			op: 'query',
			id: 0,
			projectId: this.projectId,
			method,
			args
		});
		return new Promise<T>((resolve, reject) => {
			this.inputCommands.enqueue(
				() => {
					this.broker
						.query<T>((id) => ({ ...message, id }) as MainToWorker, transfer)
						.then(resolve, reject);
				},
				() => reject(new Error('Aqueduct project closed before query was dispatched'))
			);
		});
	}

	// --- Document & sync --------------------------------------------------

	edit(patchJson: string, coalesceId: string | null) {
		this.cmd('edit', patchJson, coalesceId);
	}
	editSystem(patchJson: string) {
		this.cmd('editSystem', patchJson);
	}
	addNode(nodeId: string, nodeJson: string) {
		this.cmd('addNode', nodeId, nodeJson);
	}
	removeNode(nodeId: string) {
		this.cmd('removeNode', nodeId);
	}
	removeNodes(nodeIdsJson: string) {
		this.cmd('removeNodes', nodeIdsJson);
	}
	setValue(path: string, valueJson: string, coalesceId: string | null = null) {
		this.cmd('setValue', path, valueJson, coalesceId);
	}
	setValueLocal(path: string, valueJson: string, coalesceId: string) {
		this.cmd('setValueLocal', path, valueJson, coalesceId);
	}
	setValueSystem(path: string, valueJson: string) {
		this.cmd('setValueSystem', path, valueJson);
	}
	setParamsBatch(nodeId: string, paramsJson: string, coalesceId: string | null) {
		this.cmd('setParamsBatch', nodeId, paramsJson, coalesceId);
	}
	setParamsBatchSilent(nodeId: string, paramsJson: string, coalesceId: string | null) {
		this.cmd('setParamsBatchSilent', nodeId, paramsJson, coalesceId);
	}
	beginLocalTransaction(kind: string) {
		this.cmd('beginLocalTransaction', kind);
	}
	endLocalTransaction() {
		this.cmd('endLocalTransaction');
	}
	flushBufferedSync() {
		this.cmd('flushBufferedSync');
	}
	loadGraphToml(toml: string) {
		this.clipboardLifetime = crypto.randomUUID();
		this.cmd('loadGraphToml', toml);
	}
	/** Load a document with an acknowledgement after any requested custom-node
	 * compiler installation and the document mutation itself have completed.
	 * Deterministic capture/export flows use this before issuing render state. */
	loadGraphTomlAndWait(toml: string): Promise<void> {
		this.clipboardLifetime = crypto.randomUUID();
		return this.query('loadGraphToml', [toml]);
	}
	loadGraphJson(contentJson: string) {
		this.clipboardLifetime = crypto.randomUUID();
		this.cmd('loadGraphJson', contentJson);
	}
	undo() {
		this.cmd('undo');
	}
	redo() {
		this.cmd('redo');
	}
	connect() {
		if (this.closed) return;
		this.broker.post({ op: 'connect', projectId: this.projectId });
	}
	disconnect() {
		if (this.closed) return;
		this.broker.post({ op: 'disconnect', projectId: this.projectId });
	}
	exportToml(): Promise<string> {
		return this.query('exportToml');
	}
	contentJson(): Promise<string> {
		return this.query('contentJson');
	}
	computeLayout(): Promise<string> {
		return this.query('computeLayout');
	}
	// --- Rendering (orchestrated by the worker host) ----------------------

	/** Cached-replay render + dependent-surface refresh + diagnostics, rAF
	 *  coalesced in the worker (the old per-component scheduleRender). */
	scheduleRender() {
		this.postInput({ op: 'scheduleRender', projectId: this.projectId });
	}
	/** Full document render (slow path: load, resolution change, export). */
	renderFullDocument() {
		this.postInput({ op: 'renderFullDocument', projectId: this.projectId });
	}
	resolvePhoneResolutionLoad(choice: PhoneResolutionLoadChoice) {
		this.postInput({
			op: 'resolvePhoneResolutionLoad',
			projectId: this.projectId,
			choice
		});
	}
	setUserResolution(resolution: number, persistent: boolean) {
		this.telemetry.log(
			'info',
			`[aqueduct] Resolution change requested: project=${this.projectId} resolution=${resolution} persistent=${persistent}`
		);
		this.postInput({
			op: 'setUserResolution',
			projectId: this.projectId,
			resolution,
			persistent
		});
	}
	prepareGraphPipelines(): Promise<void> {
		return this.query('prepareGraphPipelines').then(() => undefined);
	}
	prepareRendererPipelines(profile: RendererPipelineProfile): Promise<void> {
		return this.query('prepareRendererPipelines', [profile]).then(() => undefined);
	}
	prepareStartupPipelines(profile: RendererPipelineProfile): Promise<void> {
		return this.prepareRendererPipelines(profile).then(() => this.prepareGraphPipelines());
	}
	/** Gate automatic animation of time-dependent documents. Disabling holds
	 *  the last rendered frame; explicit renders still work. */
	setAnimationEnabled(enabled: boolean) {
		if (this.closed) return;
		this.broker.post({ op: 'setAnimationEnabled', projectId: this.projectId, enabled });
	}
	resetTime() {
		this.cmd('resetTime');
		this.scheduleRender();
	}
	setTimeOverride(time: number | null) {
		this.cmd('setTimeOverride', time);
	}
	/** Render one explicit expression-clock state and resolve only after its
	 *  dependent surfaces have crossed the shared GPU fence. Capture and
	 *  benchmark clients use this instead of depending on animation cadence. */
	renderAtTimeAndWait(time: number | null): Promise<void> {
		return this.query('renderAtTimeAndWait', [time]).then(() => undefined);
	}

	// --- Surfaces ---------------------------------------------------------

	/** Register worker-owned rendering. Most surfaces transfer their HTML
	 *  canvas once; callers must never resize those transferred canvases.
	 *  The editor instead transfers detached canvases and retains bitmap
	 *  receivers here, so images and DOM geometry commit in the same turn. */
	attachCanvas(surface: AttachSurface, canvas: HTMLCanvasElement) {
		if (this.closed) return;
		bootTimelineLog(`engine: attach ${surface.kind}`, { projectId: this.projectId });
		let offscreen: OffscreenCanvas;
		if (surface.kind === 'editor') {
			const foregroundContext = canvas.getContext('bitmaprenderer');
			if (!foregroundContext) throw new Error('Editor bitmap presentation is unavailable');
			surface = { ...surface, presentationId: ++this.editorPresentationId };
			this.editorPresenter = {
				id: surface.presentationId!,
				foreground: foregroundContext
			};
			offscreen = new OffscreenCanvas(canvas.width, canvas.height);
		} else {
			offscreen = canvas.transferControlToOffscreen();
		}
		this.broker.post(
			{
				op: 'attachSurface',
				projectId: this.projectId,
				surface,
				canvas: offscreen
			},
			[offscreen]
		);
	}
	detachSurface(kind: SurfaceKind, id?: string) {
		if (kind === 'editor') this.editorPresenter = null;
		if (this.closed) return;
		this.broker.post({ op: 'detachSurface', projectId: this.projectId, kind, id });
	}
	resizeSurface(
		kind: SurfaceKind,
		width: number,
		height: number,
		dpr: number,
		id?: string,
		extra?: unknown[]
	) {
		if (this.closed) return;
		this.broker.post({
			op: 'resizeSurface',
			projectId: this.projectId,
			kind,
			id,
			width,
			height,
			dpr,
			extra
		});
	}
	/** PNG screenshot of a surface, rendered and cropped worker-side. */
	surfaceScreenshot(
		kind: SurfaceKind,
		render?: MaterialViewportParams
	): Promise<Uint8Array | null> {
		return this.broker.query((id) => ({
			op: 'surfaceScreenshot',
			id,
			projectId: this.projectId,
			kind,
			render
		}));
	}

	// --- Editor -----------------------------------------------------------

	editorPointer(ev: EditorPointerCmd) {
		this.postInput({ op: 'editorPointer', projectId: this.projectId, ev });
	}
	editorNavigate(x: number, y: number, dx: number, dy: number, factor: number): void {
		this.postInput({ op: 'editorNavigate', projectId: this.projectId, x, y, dx, dy, factor });
	}
	editorWheel(x: number, y: number, deltaY: number) {
		this.postInput({ op: 'editorWheel', projectId: this.projectId, x, y, deltaY });
	}
	editorCopyFragment(source: string): Promise<string | undefined> {
		return this.query('editorCopyFragment', [source]);
	}
	editorPasteFragment(text: string, destination: string, x: number, y: number): Promise<void> {
		return this.query('editorPasteFragment', [text, destination, x, y]);
	}
	editorKey(key: string) {
		this.postInput({ op: 'editorKey', projectId: this.projectId, key });
	}
	editorLibraryDrag(x: number, y: number, kind: string): void {
		this.cmd('editorLibraryDrag', x, y, kind);
	}
	editorBeginNodePlacement(nodeJson: string): void {
		this.cmd('editorBeginNodePlacement', nodeJson);
	}
	editorCompleteNodePicker(requestId: string, nodeId: string, nodeJson: string): void {
		this.cmd('editorCompleteNodePicker', requestId, nodeId, nodeJson);
	}
	editorLibraryDrop(x: number, y: number, nodeId: string, nodeJson: string): void {
		this.cmd('editorLibraryDrop', x, y, nodeId, nodeJson);
	}
	editorCompleteConnectionNode(requestId: string, nodeId: string, nodeJson: string) {
		this.cmd('editorCompleteConnectionNode', requestId, nodeId, nodeJson);
	}
	editorCancelConnectionNode(requestId: string) {
		this.cmd('editorCancelConnectionNode', requestId);
	}
	editorSelectOutput(outputName: string) {
		this.cmd('editorSelectOutput', outputName);
	}
	editorSelectNode(nodeId: string): void {
		this.cmd('editorSelectNode', nodeId);
	}
	editorCancelNote(id: string): void {
		this.cmd('editorCancelNote', id);
	}

	editorSelectNote(id: string, additive: boolean): void {
		this.cmd('editorSelectNote', id, additive);
	}
	editorCommitNote(id: string, content: string, position: [number, number] | null): void {
		this.cmd('editorCommitNote', id, content, JSON.stringify(position));
	}
	editorPortalAction(action: PortalAction, portalId: string) {
		this.cmd('editorPortalAction', action, portalId);
	}
	outputTrayPointer(ev: EditorPointerCmd) {
		this.postInput({ op: 'outputTrayPointer', projectId: this.projectId, ev });
	}
	editorSetTheme(dark: boolean, isMobile: boolean) {
		this.cmd('editorSetTheme', dark, isMobile);
	}
	editorSetReadOnly(readOnly: boolean) {
		this.cmd('editorSetReadOnly', readOnly);
	}
	editorSetFitMaxZoom(maxZoom: number) {
		this.cmd('editorSetFitMaxZoom', maxZoom);
	}
	editorSetFitZoomScale(zoomScale: number) {
		this.cmd('editorSetFitZoomScale', zoomScale);
	}
	editorGraphChanged() {
		this.cmd('editorGraphChanged');
	}
	editorSetNodeTimings(json: string | null) {
		this.cmd('editorSetNodeTimings', json);
	}
	editorCompensatePanelResize(dx: number) {
		this.cmd('editorCompensatePanelResize', dx);
	}
	editorDebugState(): Promise<string> {
		return this.query('editorDebugState');
	}
	/** Screen (client-rect-relative) coords to graph coords via the mirrored
	 *  view transform; null until the first editor frame arrives. */
	screenToGraph(relX: number, relY: number): { x: number; y: number } | null {
		const t = this.viewTransform;
		if (!t) return null;
		return { x: (relX - t[0]) / t[2], y: (relY - t[1]) / t[2] };
	}

	// --- Material viewport ------------------------------------------------

	renderMaterialViewport(params: MaterialViewportParams) {
		if (this.closed) return;
		this.broker.post({ op: 'mvRender', projectId: this.projectId, params });
	}
	setMaterialViewportSourceMode(mode: 'graph-outputs' | 'external-textures') {
		if (this.closed) return;
		this.broker.post({ op: 'mvSetSourceMode', projectId: this.projectId, mode });
	}
	setMaterialViewportExternalTexture(
		role: string,
		width: number,
		height: number,
		rgba: Uint8Array
	) {
		if (this.closed) return;
		const copy = rgba.slice();
		this.broker.post(
			{
				op: 'mvSetExternalTexture',
				projectId: this.projectId,
				role,
				width,
				height,
				rgba: copy.buffer
			},
			[copy.buffer]
		);
	}
	clearMaterialViewportExternalTexture(role: string) {
		if (this.closed) return;
		this.broker.post({ op: 'mvClearExternalTexture', projectId: this.projectId, role });
	}
	clearMaterialViewportExternalTextures() {
		if (this.closed) return;
		this.broker.post({ op: 'mvClearExternalTexture', projectId: this.projectId, role: null });
	}
	setMaterialViewportEnvironmentQuality(quality: number) {
		this.cmd('setMaterialViewportEnvironmentQuality', quality);
	}
	readMaterialViewportTiming(): Promise<{ avg: number; count: number; triangles: number } | null> {
		return this.query('readMaterialViewportTiming');
	}

	// --- 2D preview -------------------------------------------------------

	setPreviewSource(nodeId: string) {
		if (this.closed) return;
		this.broker.post({ op: 'previewSetSource', projectId: this.projectId, nodeId });
	}
	blitPreview(params: PreviewParams) {
		if (this.closed) return;
		this.broker.post({ op: 'previewBlit', projectId: this.projectId, params });
	}
	/** Update the stored preview params without drawing. The worker's
	 *  post-render refresh blits with them, so a view transform computed for
	 *  a not-yet-rendered texture (resolution change) never touches the
	 *  stale frame on screen. */
	setPreviewParams(params: PreviewParams) {
		if (this.closed) return;
		this.broker.post({ op: 'previewSetParams', projectId: this.projectId, params });
	}
	/** Hover target for the pixel inspector; (-1, -1) disables. Readbacks
	 *  arrive via onHoverPixel. */
	previewHover(x: number, y: number) {
		if (this.closed) return;
		this.broker.post({ op: 'previewHover', projectId: this.projectId, x, y });
	}
	setOutputTrayLayout(layoutJson: string, dpr: number, darkMode: boolean, selectedNodeId: string) {
		this.cmd('setOutputTrayLayout', layoutJson, dpr, darkMode, selectedNodeId);
	}

	// --- Widget canvases --------------------------------------------------

	setEditorArtifactSource(instanceId: string, nodeId: string, artifactName: string) {
		this.cmd('setEditorArtifactSource', instanceId, nodeId, artifactName);
	}
	setEditorArtifactDarkMode(dark: boolean) {
		this.cmd('setEditorArtifactDarkMode', dark);
	}
	renderColorPickerSV(id: string, hue: number) {
		this.cmd('renderColorPickerSV', id, hue);
	}
	renderColorPickerHue(id: string) {
		this.cmd('renderColorPickerHue', id);
	}
	renderGradientBar(id: string, stopsJson: string, interpolation: number) {
		this.cmd('renderGradientBar', id, stopsJson, interpolation);
	}

	/** Every clock in the system, in one call: the engine expression clock
	 *  (what `t` in graph expressions reads), its override, the raw uptime,
	 *  and - when this project hosts a demo - the demo clock's phase and
	 *  warped time (engine/demoClock.ts). The first tool to reach for when
	 *  any animation misbehaves. */
	timeDebug(resetFrameMaxima = false): Promise<{
		expressionClock: number;
		override: number | null;
		uptimeSecs: number;
		demoClock: { phase: string; rawMs: number | null; warpedMs: number } | null;
		timeDependent: boolean;
		autoAnimationActive: boolean;
		animationEnabled: boolean;
		animationFailed: boolean;
		pageHidden: boolean;
		frameWorkPasses: number;
		graphRenderCount: number;
		previewFpsLimit: number;
		demoRenderCount: number;
		materialRenderCount: number;
		materialRenderGapMaxMs: number;
		materialRenderGapsOver25Ms: number;
		materialRenderGapsOver100Ms: number;
		renderRequestCount: number;
		coalescedRenderRequestCount: number;
		gpuFrameFenceInFlight: boolean;
		gpuFrameFenceCount: number;
		gpuFrameFenceMaxMs: number;
		gpuFrameFenceOver25Ms: number;
		gpuFrameFenceOver100Ms: number;
		gpuBackpressureDeferrals: number;
		gpuBackpressureFrameSkips: number;
		workerFrameGapMaxMs: number;
		workerFrameGapsOver25Ms: number;
		workerFrameGapsOver100Ms: number;
		workerNowMs: number;
		phoneResolutionHeld: boolean;
		workingResolutionOverride: number | null;
	}> {
		return this.query('timeDebug', [resetFrameMaxima]);
	}

	// --- Marketing demos --------------------------------------------------

	/** Release the demo clock from its t=0 hold once the poster swap is on
	 *  glass; animation advances from exactly the pose the poster showed. */
	demoStart() {
		if (this.closed) return;
		this.broker.post({ op: 'demoStart', projectId: this.projectId });
	}

	demoLoop(kind: 'landingDemo' | 'dropletDemo', loop: LandingDemoLoop) {
		if (this.closed) return;
		this.broker.post({ op: 'demoLoop', projectId: this.projectId, kind, loop });
	}
	demoConfig(kind: 'landingDemo' | 'dropletDemo', configJson: string) {
		if (this.closed) return;
		this.broker.post({ op: 'demoConfig', projectId: this.projectId, kind, configJson });
	}
	demoPointer(kind: 'landingDemo' | 'dropletDemo', phase: 0 | 1 | 2, x: number, y: number) {
		if (this.closed) return;
		this.broker.post({ op: 'demoPointer', projectId: this.projectId, kind, phase, x, y });
	}

	// --- Exports & profiling ---------------------------------------------

	/** Small GPU-resampled source preview, independent of node output/format. */
	imageAssetPreview(source: string): Promise<Uint8Array> {
		return this.query('imageAssetPreview', [source]);
	}

	exportOutputsZip(name: string): Promise<Uint8Array> {
		return this.query('exportOutputsZip', [name]);
	}
	exportNodePng(nodeId: string): Promise<Uint8Array> {
		return this.query('exportNodePng', [nodeId]);
	}
	exportOutputPng(outputName: string): Promise<Uint8Array> {
		return this.query('exportOutputPng', [outputName]);
	}
	exportMaterialThumbnailPair(size: number, tinySize: number): Promise<Uint8Array[]> {
		return this.query('exportMaterialThumbnailPair', [size, tinySize]);
	}
	timeNodePasses(nodeId: string): Promise<unknown> {
		return this.query('timeNodePasses', [nodeId]);
	}
	setLogConfig(configJson: string) {
		this.cmd('setLogConfig', configJson);
	}
	setGraphProfilingEnabled(enabled: boolean) {
		this.cmd('setGraphProfilingEnabled', enabled);
	}
	setGraphPerNodeProfilingEnabled(enabled: boolean) {
		this.cmd('setGraphPerNodeProfilingEnabled', enabled);
	}
	setMaterialViewportGpuTimingEnabled(enabled: boolean) {
		this.cmd('setMaterialViewportGpuTimingEnabled', enabled);
	}
	graphProfilingSupported(): Promise<boolean> {
		return this.query('graphProfilingSupported');
	}
	gpuTimestampsSupported(): Promise<boolean> {
		return this.query('gpuTimestampsSupported');
	}
	readGraphProfile(): Promise<unknown> {
		return this.query('readGraphProfile');
	}
	readVramStats(): Promise<unknown> {
		return this.query('readVramStats');
	}

	readBlitTiming(): Promise<unknown> {
		return this.query('readBlitTiming');
	}
	readMaterialViewportGpuTiming(): Promise<unknown> {
		return this.query('readMaterialViewportGpuTiming');
	}
	dumpProfile(): Promise<string> {
		return this.query('dumpProfile');
	}
}
